import { Injectable } from '@angular/core';
import { RestService, Rest, PagedResultDto, ConfigStateService, SessionStateService } from '@abp/ng.core';
import { BehaviorSubject, Observable, tap, switchMap, of, catchError, filter, take } from 'rxjs';
import {
  ImpersonatableUserDto,
  ImpersonationStatusDto,
  ImpersonationResultDto,
  ImpersonationLogDto,
  GetImpersonationLogsInput,
} from '../models/impersonation.models';

const IMPERSONATION_TOKEN_KEY = 'impersonation_token';
const IMPERSONATION_STATUS_KEY = 'impersonation_status';

@Injectable({
  providedIn: 'root',
})
export class ImpersonationService {
  apiName = 'Default';
  private impersonationStatus$ = new BehaviorSubject<ImpersonationStatusDto | null>(null);
  private isValidating = false;

  constructor(
    private restService: RestService,
    private configStateService: ConfigStateService,
    private sessionState: SessionStateService
  ) {
    // Initialize status from localStorage immediately on service creation
    // This ensures the banner shows correctly on page refresh before API validation
    this.initializeFromLocalStorage();
    this.initializeAuthSubscription();
  }

  /**
   * Initialize impersonation status from localStorage immediately
   * This is called before any API calls to ensure correct state on page refresh
   */
  private initializeFromLocalStorage(): void {
    const storedStatus = this.getStoredStatus();
    if (storedStatus && this.impersonationToken) {
      this.impersonationStatus$.next(storedStatus);
    }
  }

  /**
   * Subscribe to authentication state changes
   */
  private initializeAuthSubscription(): void {
    // Listen for logout - clear impersonation when user logs out
    this.configStateService.getDeep$('currentUser.isAuthenticated').subscribe(isAuthenticated => {
      if (!isAuthenticated) {
        // User logged out - clear any impersonation state
        this.clearStoredStatus();
      } else if (this.impersonationToken && !this.isValidating) {
        // User is authenticated and has stored token - validate it
        this.validateStoredImpersonation();
      }
    });
  }

  /**
   * Validate stored impersonation token with the server
   */
  private validateStoredImpersonation(): void {
    const token = this.impersonationToken;
    if (!token) {
      this.clearStoredStatus();
      return;
    }

    this.isValidating = true;

    this.getStatus().pipe(
      catchError(() => {
        // Token is invalid or expired - clear local state
        this.clearStoredStatus();
        return of(null);
      })
    ).subscribe(status => {
      this.isValidating = false;
      if (!status || !status.isImpersonating) {
        this.clearStoredStatus();
      }
    });
  }

  /**
   * Get current impersonation status as observable
   */
  get status$(): Observable<ImpersonationStatusDto | null> {
    return this.impersonationStatus$.asObservable();
  }

  /**
   * Get current impersonation status value
   */
  get currentStatus(): ImpersonationStatusDto | null {
    return this.impersonationStatus$.value;
  }

  /**
   * Check if currently impersonating
   */
  get isImpersonating(): boolean {
    return this.impersonationStatus$.value?.isImpersonating ?? false;
  }

  /**
   * Get the impersonation token for API calls
   */
  get impersonationToken(): string | null {
    return localStorage.getItem(IMPERSONATION_TOKEN_KEY);
  }

  /**
   * Check if user is currently authenticated
   */
  get isAuthenticated(): boolean {
    return !!this.configStateService.getDeep('currentUser.isAuthenticated');
  }

  /**
   * Parse date string ensuring UTC timezone
   */
  private parseUtcDate(dateStr: string | Date | undefined): Date | null {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    // If the date string doesn't have timezone info, treat it as UTC
    if (!dateStr.endsWith('Z') && !dateStr.includes('+')) {
      dateStr = dateStr + 'Z';
    }
    return new Date(dateStr);
  }

  /**
   * Load stored impersonation status from localStorage (only for checking, doesn't set state)
   */
  private getStoredStatus(): ImpersonationStatusDto | null {
    const storedStatus = localStorage.getItem(IMPERSONATION_STATUS_KEY);
    if (storedStatus) {
      try {
        const status = JSON.parse(storedStatus) as ImpersonationStatusDto;
        // Check if session is expired (with proper UTC handling)
        const expiresAt = this.parseUtcDate(status.expiresAt);
        if (expiresAt && expiresAt < new Date()) {
          return null;
        }
        return status;
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Store impersonation status to localStorage
   */
  private storeStatus(status: ImpersonationStatusDto, token?: string): void {
    localStorage.setItem(IMPERSONATION_STATUS_KEY, JSON.stringify(status));
    if (token) {
      localStorage.setItem(IMPERSONATION_TOKEN_KEY, token);
    }
    this.impersonationStatus$.next(status);
  }

  /**
   * Clear stored impersonation status
   */
  private clearStoredStatus(): void {
    localStorage.removeItem(IMPERSONATION_STATUS_KEY);
    localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    this.impersonationStatus$.next(null);
  }

  /**
   * Force clear impersonation state without calling API
   * Use when API call fails or session is invalid
   */
  forceEndImpersonation(): Observable<void> {
    this.clearStoredStatus();
    return this.configStateService.refreshAppState().pipe(
      switchMap(() => of(undefined as void))
    );
  }

  /**
   * Get list of users that can be impersonated
   */
  getImpersonatableUsers(
    input?: { skipCount?: number; maxResultCount?: number; sorting?: string },
    config?: Partial<Rest.Config>
  ): Observable<PagedResultDto<ImpersonatableUserDto>> {
    return this.restService.request<any, PagedResultDto<ImpersonatableUserDto>>(
      {
        method: 'GET',
        url: '/api/app/impersonation/impersonatable-users',
        params: {
          skipCount: input?.skipCount ?? 0,
          maxResultCount: input?.maxResultCount ?? 1000, // Default to large number to get all
          sorting: input?.sorting,
        },
      },
      { apiName: this.apiName, ...config }
    );
  }

  /**
   * Start impersonating a user
   */
  startImpersonation(userId: string, config?: Partial<Rest.Config>): Observable<ImpersonationResultDto> {
    return this.restService
      .request<any, ImpersonationResultDto>(
        {
          method: 'POST',
          url: `/api/app/impersonation/start/${userId}`,
        },
        { apiName: this.apiName, ...config }
      )
      .pipe(
        tap(result => {
          if (result.success && result.impersonationToken) {
            const status: ImpersonationStatusDto = {
              isImpersonating: true,
              impersonatedUserId: result.impersonatedUser?.id,
              impersonatedUserName: result.impersonatedUser?.userName,
              impersonatedUserFullName: result.impersonatedUser?.name,
              expiresAt: result.expiresAt,
            };
            this.storeStatus(status, result.impersonationToken);
          }
        }),
        // Refresh app configuration to get impersonated user's permissions
        switchMap(result => {
          if (result.success) {
            return this.configStateService.refreshAppState().pipe(
              switchMap(() => of(result))
            );
          }
          return of(result);
        })
      );
  }

  /**
   * End current impersonation session
   */
  endImpersonation(config?: Partial<Rest.Config>): Observable<void> {
    const token = this.impersonationToken;
    const headers = token ? { 'X-Impersonation-Token': token } : undefined;

    return this.restService
      .request<any, void>(
        {
          method: 'POST',
          url: '/api/app/impersonation/end',
          headers,
        },
        { apiName: this.apiName, ...config }
      )
      .pipe(
        tap(() => {
          this.clearStoredStatus();
        }),
        // Refresh app configuration to restore original user's permissions
        switchMap(() => this.configStateService.refreshAppState().pipe(
          switchMap(() => of(undefined as void))
        ))
      );
  }

  /**
   * Get current impersonation status from server
   */
  getStatus(config?: Partial<Rest.Config>): Observable<ImpersonationStatusDto> {
    const token = this.impersonationToken;
    const headers = token ? { 'X-Impersonation-Token': token } : undefined;

    return this.restService
      .request<any, ImpersonationStatusDto>(
        {
          method: 'GET',
          url: '/api/app/impersonation/status',
          headers,
        },
        { apiName: this.apiName, ...config }
      )
      .pipe(
        tap(status => {
          if (status.isImpersonating) {
            this.storeStatus(status, token || undefined);
          } else {
            this.clearStoredStatus();
          }
        })
      );
  }

  /**
   * Get impersonation logs (admin only)
   */
  getLogs(
    input: GetImpersonationLogsInput,
    config?: Partial<Rest.Config>
  ): Observable<PagedResultDto<ImpersonationLogDto>> {
    return this.restService.request<any, PagedResultDto<ImpersonationLogDto>>(
      {
        method: 'GET',
        url: '/api/app/impersonation/logs',
        params: {
          impersonatorUserId: input.impersonatorUserId,
          impersonatedUserId: input.impersonatedUserId,
          startDate: input.startDate?.toISOString(),
          endDate: input.endDate?.toISOString(),
          skipCount: input.skipCount,
          maxResultCount: input.maxResultCount,
          sorting: input.sorting,
        },
      },
      { apiName: this.apiName, ...config }
    );
  }

  /**
   * Refresh impersonation status from server
   */
  refreshStatus(): void {
    if (this.impersonationToken) {
      this.getStatus().subscribe();
    }
  }
}
